import { INTERNAL_WIRING_DEBT_MESSAGE, isInternalDebtMessage } from "./internal-debt-message";

/**
 * Extract a short, user-actionable summary from a sync-engine push failure.
 *
 * Adapter errors (Apideck/Xero/QuickBooks/Sage, Zoho Books, Wafeq, Odoo,
 * Autocount, ERPNext) arrive at the AR/AP record-payment seams as one long
 * single-line string — e.g. the Apideck envelope:
 *   "Xero Connector Returned Error | Detail: {…} | Type: ConnectorExecutionError
 *    | Downstream: … | Status: 400"
 *
 * The raw form is fine for support but unreadable on a bill / invoice
 * timeline or in a toast. We try, in order:
 *   0. Internal wiring-debt strings (OCT-XXXX / D##) — suppressed and replaced
 *      with a stable user-safe message so the raw ticket reference never leaks
 *      to any client-visible surface.
 *   1. Xero — nested `Elements[].LineItems[].ValidationErrors[].Message`
 *      (most specific, e.g. "Please enter a description or line item details.")
 *   2. Xero — nested `Elements[].ValidationErrors[].Message`
 *   3. QuickBooks — `Fault.Error[].Detail` (preferred — actionable phrase,
 *      e.g. "Required parameter Line.Amount is missing in the request") and
 *      `Fault.Error[].Message` (fallback — generic "Required param missing"
 *      template only used when QB omits Detail).
 *   4. Generic top-level `error.Message`
 *   5. The `Downstream:` segment — `ValidationError:` entries preferred over
 *      the generic exception description that precedes them (OCT-1366).
 *   6. Plain-text `Detail:` segment when no Downstream is present (OCT-1366).
 *   7. A safe fallback
 *
 * Lives in `shared/` so both AR/AP server handlers and the client
 * `SyncStatusIndicator` render the same friendly text.
 */
export function parsePushFailureMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);

  // Step 0: suppress internal wiring-debt markers before any other parsing.
  // Covers ledger errorMessage columns stored by execution.ts and the FE
  // render path in sync-status-indicator.tsx -> summarizeErrorMessage.
  if (isInternalDebtMessage(raw)) return INTERNAL_WIRING_DEBT_MESSAGE;

  const detailMatch = raw.match(/Detail:\s*({[\s\S]+?})\s*(?:\|\s*Type:|\|\s*Downstream:|\|\s*Status:|$)/);
  if (detailMatch && detailMatch[1]) {
    try {
      const parsed = JSON.parse(detailMatch[1]) as Record<string, unknown>;
      const error = parsed["error"] as
        | {
          Message?: string;
          Elements?: Array<{
            ValidationErrors?: Array<{ Message?: string; }>;
            LineItems?: Array<{ ValidationErrors?: Array<{ Message?: string; }>; }>;
          }>;
          Fault?: {
            Error?: Array<{ Message?: string; Detail?: string; code?: string; element?: string; }>;
          };
        }
        | undefined;
      const seen = new Set<string>();
      const collect = (msg: string | undefined) => {
        if (typeof msg === "string" && msg.length > 0) seen.add(msg);
      };
      for (const el of error?.Elements ?? []) {
        for (const li of el.LineItems ?? []) {
          for (const v of li.ValidationErrors ?? []) collect(v.Message);
        }
        for (const v of el.ValidationErrors ?? []) collect(v.Message);
      }
      let messages = Array.from(seen);
      if (messages.length > 0) return messages.slice(0, 3).join("; ");

      // QuickBooks ValidationFault shape: `Fault.Error[]` with `Detail`
      // carrying the actionable phrase and `Message` the generic template.
      // Prefer Detail; fall back to Message when Detail is absent.
      const qbErrors = error?.Fault?.Error ?? [];
      const qbSeen = new Set<string>();
      for (const qe of qbErrors) {
        if (typeof qe.Detail === "string" && qe.Detail.length > 0) qbSeen.add(qe.Detail);
        else if (typeof qe.Message === "string" && qe.Message.length > 0) qbSeen.add(qe.Message);
      }
      messages = Array.from(qbSeen);
      if (messages.length > 0) return messages.slice(0, 3).join("; ");

      if (typeof error?.Message === "string" && error.Message.length > 0) return error.Message;
    } catch {
      // fall through to text-based extraction
    }
  }

  const downstreamMatch = raw.match(/Downstream:\s*([^|]+?)(?:\s*\|\s*Status:|$)/);
  if (downstreamMatch && downstreamMatch[1]) {
    const downstream = downstreamMatch[1].trim();
    // ValidationError: entries carry the specific provider reason (e.g. "Account
    // currency must match organisation currency" from Xero). Prefer them over the
    // generic exception description that precedes them.
    const veMatches = [...downstream.matchAll(/;\s*ValidationError:\s*([^;]+)/gi)];
    if (veMatches.length > 0) {
      const msgs = veMatches.map((m) => (m[1] ?? "").trim()).filter(Boolean);
      if (msgs.length > 0) return msgs.slice(0, 3).join("; ");
    }
    return downstream.replace(/;\s*ValidationError:.*$/i, "").trim();
  }

  // Plain-text Detail: segment - produced when extractApideckErrorDetails extracts
  // validation messages as readable text rather than JSON (per OCT-1375). Tried
  // after Downstream so a generic "Validation failed" string does not shadow a
  // more specific Downstream ValidationError message.
  const detailPlainMatch = raw.match(
    /(?:^|\|\s*)Detail:\s*([^|{][^|]*?)(?=\s*\|\s*(?:Type:|Downstream:|Status:)|\s*$)/,
  );
  if (detailPlainMatch && detailPlainMatch[1]) {
    const text = detailPlainMatch[1].trim();
    if (text) return text;
  }

  // OCT-14720 - OCTA's own pre-push validation errors ("No deposit account
  // configured for this payment - ...") reach the ledger as
  // "<reason> | Status: 400": extractApideckErrorDetails appends the local
  // error's status. Only a status segment follows the reason, so the reason
  // is the whole message; a provider's own "Connector Returned Error" head
  // is not a reason and still falls back below.
  const reasonThenStatus = raw.match(/^([^|{]+?)\s*\|\s*Status:\s*\d{3}\s*$/);
  if (reasonThenStatus?.[1] && !/Connector Returned Error/i.test(reasonThenStatus[1])) {
    return reasonThenStatus[1].trim();
  }

  // OCT-1972 — Zoho native push warnings (e.g. "Line item GL code "(none)"
  // not matched — using default income account") don't carry an Apideck-style
  // envelope. If the raw message clearly isn't a connector envelope (no
  // "Connector Returned Error", no "Status: <code>", no "Detail: {"), pass
  // it through verbatim so users see the actionable phrase instead of the
  // generic "rejected" fallback.
  const looksLikeConnectorEnvelope = /Connector Returned Error|Status:\s*\d|Detail:\s*\{/i.test(raw);
  if (raw && !looksLikeConnectorEnvelope) return raw;

  return "The accounting system rejected this change.";
}
